import React, { useEffect, useRef, useState } from "react";
import { Empty } from "antd";
import { Timeline } from "vis-timeline/esnext";
import { DataSet } from "vis-data/esnext";
import "vis-timeline/styles/vis-timeline-graph2d.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment-timezone";
import WidgetWrapper from "../../components/WidgetWrapper";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { FLIGHT_STATUS_COLORS } from "../../../flightOperation/Monitor/constants/flightConstants";
import "../../../flightOperation/Monitor/components/FlightTimelineView.css"; // Reuse CSS

dayjs.extend(utc);
dayjs.extend(timezone);

const FlightTimelineWidget = () => {
    const timelineContainer = useRef(null);
    const timelineInstance = useRef(null);
    const [loading, setLoading] = useState(false);
    const [flights, setFlights] = useState([]);

    // Calculate date range: Today-1 to Today+7
    const startDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    const endDate = dayjs().add(7, "day").format("YYYY-MM-DD");

    const fetchData = async () => {
        setLoading(true);
        try {
            // Use getFlightOperationsWithTransactions as it is the robust endpoint available
            const res = await flightOperationFlightService.getFlightOperationsWithTransactions({
                start_date: startDate,
                end_date: endDate,
                page_size: 500, // Max 500 allowed
                include_transactions: false,
            });
            setFlights(res.data?.items || []);
        } catch (error) {
            console.error("Failed to fetch timeline data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();

        // 组件卸载时清理 timeline 实例
        return () => {
            if (timelineInstance.current) {
                timelineInstance.current.destroy();
                timelineInstance.current = null;
            }
        };
    }, []);

    // Prepare Data for Vis Timeline
    useEffect(() => {
        if (!timelineContainer.current || loading || flights.length === 0) return;

        // 延迟初始化，确保容器已完成布局
        const initTimer = setTimeout(() => {
            if (!timelineContainer.current) return;

            // Groups (Aircraft)
            const aircraftSet = new Set(flights.map((f) => f.aircraft_reg || "Unassigned"));
            const groups = new DataSet(
                Array.from(aircraftSet)
                    .sort()
                    .map((reg) => ({
                        id: reg,
                        content: `<span style="font-weight:bold; font-size: 12px;">${reg}</span>`,
                        style: "font-size: 12px;",
                    })),
            );

            // Items (Flights) - 过滤掉 REPLACED、PLAN_CANCELLED 和 CANCELLED 状态的航班
            const items = new DataSet(
                flights
                    .filter((f) => {
                        const status = f.status || "SCHEDULED";
                        return status !== "REPLACED" && status !== "PLAN_CANCELLED" && status !== "CANCELLED";
                    })
                    .map((f) => {
                        const start = f.std ? dayjs(f.std).toDate() : null;
                        const end = f.sta ? dayjs(f.sta).toDate() : null;

                        if (!start || !end) return null;

                        const status = f.status || "SCHEDULED";
                        const color = FLIGHT_STATUS_COLORS[status] || "#1890ff";

                        // Determine if it's actual or plan based on status
                        const isActual = ["DEPARTED", "COMPLETED", "DIVERTED"].includes(status);
                        const displayStart = isActual && f.atd ? dayjs(f.atd).toDate() : start;
                        const displayEnd = isActual && f.ata ? dayjs(f.ata).toDate() : end;

                        // Handle in-flight (no ATA yet)
                        const finalEnd = !f.ata && f.atd && f.eta ? dayjs(f.eta).toDate() : displayEnd;

                        return {
                            id: f.id,
                            group: f.aircraft_reg || "Unassigned",
                            start: displayStart,
                            end: finalEnd,
                            content: `<div class="flight-content ${isActual ? "actual" : "plan"}" style="height: 100%; display: flex; align-items: center; white-space: nowrap; overflow: hidden;">
                                <span class="flight-number" style="font-weight: bold; margin-right: 4px;">${f.flight_number}</span>
                                <span class="route" style="font-size: 0.9em; opacity: 0.9;">${f.departure_airport}-${f.arrival_airport}</span>
                            </div>`,
                            className: `vis-item flight-block ${isActual ? "flight-actual" : "flight-plan"}`,
                            style: `background-color: ${color}; border-color: ${color};`, // Let CSS handle most styles, just set color dynamically
                            title: `${f.flight_number}: ${f.departure_airport} -> ${f.arrival_airport} (${status})`,
                        };
                    })
                    .filter(Boolean),
            );

            const options = {
                orientation: "top",
                stack: false,
                horizontalScroll: true,
                zoomKey: "ctrlKey",
                maxHeight: "100%",
                start: dayjs().subtract(6, "hour").toDate(), // Default view: recent past to future
                end: dayjs().add(18, "hour").toDate(),
                zoomMin: 1000 * 60 * 60 * 2, // 2 hours
                zoomMax: 1000 * 60 * 60 * 24 * 7, // 7 days
                showCurrentTime: true,
                moment: (date) => moment.utc(date),
            };

            if (timelineInstance.current) {
                timelineInstance.current.setGroups(groups);
                timelineInstance.current.setItems(items);
                timelineInstance.current.setOptions(options);
                timelineInstance.current.redraw();
            } else {
                timelineInstance.current = new Timeline(timelineContainer.current, items, groups, options);
                // 多次强制重绘，确保渲染
                setTimeout(() => {
                    if (timelineInstance.current) {
                        timelineInstance.current.redraw();
                    }
                }, 50);
                setTimeout(() => {
                    if (timelineInstance.current) {
                        timelineInstance.current.redraw();
                    }
                }, 200);
            }
        }, 100); // 延迟100ms初始化

        // 监听窗口大小变化，自动调整 timeline
        const handleResize = () => {
            if (timelineInstance.current) {
                timelineInstance.current.redraw();
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(initTimer);
            window.removeEventListener('resize', handleResize);
        };
    }, [flights, loading]);

    return (
        <WidgetWrapper 
            title={`航班排班 UTC (${dayjs(startDate).format("MM/DD")} - ${dayjs(endDate).format("MM/DD")})`} 
            loading={loading} 
            onRefresh={fetchData} 
            bodyStyle={{ padding: 0, height: "100%", minHeight: "200px" }} 
            bordered={false}
        >
            {flights.length > 0 ? (
                <div 
                    ref={timelineContainer}
                    className="flight-timeline-vis-container"
                    style={{ 
                        height: "100%", 
                        width: "100%", 
                        minHeight: "200px",
                        position: "relative"
                    }} 
                />
            ) : (
                <Empty 
                    description="暂无排班数据" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                    style={{ marginTop: 40 }} 
                />
            )}
        </WidgetWrapper>
    );
};

export default FlightTimelineWidget;
